import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { ActiveTool } from './toolbar/use-canvas-tool';
import { SHAPE_COMPONENTS, type ShapeKind } from '@/features/canvas/engine/nodes/shape/shapes';
import { EmojiRenderer } from '@/components/emoji-renderer/emoji-renderer';
import styles from './tool-placement-ghost.module.css';
import { resolvePlacementSize } from './placement-size';

interface Props {
  activeTool: ActiveTool;
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Current canvas zoom level — ghost renders at ghostSize * zoom pixels. */
  zoom?: number;
}

function ShapeGhost({ shapeKind, width, height }: { shapeKind: ShapeKind; width: number; height: number }) {
  const ShapeComponent = SHAPE_COMPONENTS[shapeKind];
  if (!shapeKind || !ShapeComponent) return null;
  // Static 100×100 viewBox for every shape (matches the node). Pill is
  // dimension-aware only in that it gets W/H to pre-compensate its corner
  // radius for the stretch — it still draws in the 100×100 box.
  const variantDims = shapeKind === 'pill' ? { width, height } : {};
  return (
    <svg
      viewBox="0 0 100 100"
      width={width}
      height={height}
      className={styles.shapeSvg}
      preserveAspectRatio="none"
    >
      <ShapeComponent strokeDasharray="6 4" cornerRadius={0} {...variantDims} />
    </svg>
  );
}

/**
 * Renders a semi-transparent ghost of the node-to-be-placed that follows the
 * cursor when a non-select tool is active. Pure visual overlay — no RF
 * involvement, no Yjs writes. Clicking is handled by the parent's onPaneClick.
 */
export function ToolPlacementGhost({ activeTool, containerRef, zoom = 1 }: Props) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onMove = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    const onLeave = () => setPos(null);

    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);
    return () => {
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerleave', onLeave);
    };
  }, [containerRef]);

  if (activeTool.kind === 'select' || activeTool.kind === 'freedraw') return null;
  if (!pos) return null;

  // Single source of truth for placement size (per tool + shapeKind), shared
  // with the actual node-creation path in canvas-editor.
  const presetSize = activeTool.kind === 'frame' ? activeTool.size : null;
  const size = resolvePlacementSize(activeTool) ?? { width: 120, height: 120 };
  const shapeKind = activeTool.kind === 'shape'
    ? (activeTool as { kind: 'shape'; shapeKind: ShapeKind }).shapeKind
    : undefined;
  // Ghost renders at canvas-space size scaled by current zoom so it matches
  // the visual footprint of the node that will be placed on click.
  const screenW = size.width * zoom;
  const screenH = size.height * zoom;
  const frameSizeLabel = presetSize ? `${presetSize.width} × ${presetSize.height}` : null;

  // Stable key per tool selection so the pop animation re-fires each time the
  // user picks a new emoji/sticker/gif without re-triggering on cursor moves.
  const animKey = activeTool.kind === 'emoji'
    ? `emoji-${activeTool.ref.id}`
    : activeTool.kind === 'gif'
      ? `gif-${activeTool.data.stillUrl}`
      : activeTool.kind;

  const isMediaTool = activeTool.kind === 'emoji' || activeTool.kind === 'gif';

  return (
    <div ref={overlayRef} className={styles.overlay} aria-hidden="true">
      <AnimatePresence>
        <motion.div
          key={animKey}
          className={styles.ghost}
          data-kind={activeTool.kind}
          style={{
            '--ghost-x': `${pos.x}px`,
            '--ghost-y': `${pos.y}px`,
            '--ghost-w': `${screenW}px`,
            '--ghost-h': `${screenH}px`,
            '--ghost-color': activeTool.kind === 'sticky' ? activeTool.stickyColor : undefined,
          } as React.CSSProperties}
          initial={isMediaTool ? { scale: 0.25, opacity: 0 } : false}
          animate={isMediaTool ? { scale: 1, opacity: 0.75 } : { opacity: 0.45 }}
          transition={isMediaTool ? {
            type: 'spring',
            stiffness: 400,
            damping: 22,
            mass: 0.6,
          } : { duration: 0 }}
        >
          {shapeKind && (
            <ShapeGhost shapeKind={shapeKind} width={screenW} height={screenH} />
          )}
          {activeTool.kind === 'emoji' && (
            <EmojiRenderer group={activeTool.ref.group} id={activeTool.ref.id} size={screenW} variant={activeTool.ref.variant} alt="" useSprite />
          )}
          {activeTool.kind === 'gif' && (
            <img src={activeTool.data.stillUrl} alt="" className={styles.gifGhost} />
          )}
          {frameSizeLabel && (
            <span className={styles.frameSizeLabel}>{frameSizeLabel}</span>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
